import React, { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Search, Link2, Upload } from 'lucide-react';
import { Button } from './ui/button';

// Microsoft Fluent 3D Emoji CDN base URL
const FLUENT_CDN = 'https://raw.githubusercontent.com/microsoft/fluentui-emoji/main/assets';

// 3D Fluent Emoji icons with their CDN paths
const fluent3DIcons = {
  'Business': [
    { name: 'Bar Chart', url: `${FLUENT_CDN}/Bar%20chart/3D/bar_chart_3d.png` },
    { name: 'Chart Increasing', url: `${FLUENT_CDN}/Chart%20increasing/3D/chart_increasing_3d.png` },
    { name: 'Chart Decreasing', url: `${FLUENT_CDN}/Chart%20decreasing/3D/chart_decreasing_3d.png` },
    { name: 'Briefcase', url: `${FLUENT_CDN}/Briefcase/3D/briefcase_3d.png` },
    { name: 'Office Building', url: `${FLUENT_CDN}/Office%20building/3D/office_building_3d.png` },
    { name: 'Classical Building', url: `${FLUENT_CDN}/Classical%20building/3D/classical_building_3d.png` },
    { name: 'Clipboard', url: `${FLUENT_CDN}/Clipboard/3D/clipboard_3d.png` },
    { name: 'File Folder', url: `${FLUENT_CDN}/File%20folder/3D/file_folder_3d.png` },
    { name: 'Open File Folder', url: `${FLUENT_CDN}/Open%20file%20folder/3D/open_file_folder_3d.png` },
    { name: 'Calendar', url: `${FLUENT_CDN}/Calendar/3D/calendar_3d.png` },
    { name: 'Money Bag', url: `${FLUENT_CDN}/Money%20bag/3D/money_bag_3d.png` },
    { name: 'Dollar', url: `${FLUENT_CDN}/Dollar%20banknote/3D/dollar_banknote_3d.png` },
    { name: 'Credit Card', url: `${FLUENT_CDN}/Credit%20card/3D/credit_card_3d.png` },
    { name: 'Gem Stone', url: `${FLUENT_CDN}/Gem%20stone/3D/gem_stone_3d.png` },
    { name: 'Balance Scale', url: `${FLUENT_CDN}/Balance%20scale/3D/balance_scale_3d.png` },
    { name: 'Receipt', url: `${FLUENT_CDN}/Receipt/3D/receipt_3d.png` },
    { name: 'Memo', url: `${FLUENT_CDN}/Memo/3D/memo_3d.png` },
    { name: 'Package', url: `${FLUENT_CDN}/Package/3D/package_3d.png` },
    { name: 'Shopping Cart', url: `${FLUENT_CDN}/Shopping%20cart/3D/shopping_cart_3d.png` },
    { name: 'Label', url: `${FLUENT_CDN}/Label/3D/label_3d.png` },
  ],
  'Technology': [
    { name: 'Laptop', url: `${FLUENT_CDN}/Laptop/3D/laptop_3d.png` },
    { name: 'Desktop Computer', url: `${FLUENT_CDN}/Desktop%20computer/3D/desktop_computer_3d.png` },
    { name: 'Keyboard', url: `${FLUENT_CDN}/Keyboard/3D/keyboard_3d.png` },
    { name: 'Computer Mouse', url: `${FLUENT_CDN}/Computer%20mouse/3D/computer_mouse_3d.png` },
    { name: 'Mobile Phone', url: `${FLUENT_CDN}/Mobile%20phone/3D/mobile_phone_3d.png` },
    { name: 'Floppy Disk', url: `${FLUENT_CDN}/Floppy%20disk/3D/floppy_disk_3d.png` },
    { name: 'CD', url: `${FLUENT_CDN}/Optical%20disk/3D/optical_disk_3d.png` },
    { name: 'Electric Plug', url: `${FLUENT_CDN}/Electric%20plug/3D/electric_plug_3d.png` },
    { name: 'Battery', url: `${FLUENT_CDN}/Battery/3D/battery_3d.png` },
    { name: 'Light Bulb', url: `${FLUENT_CDN}/Light%20bulb/3D/light_bulb_3d.png` },
    { name: 'Flashlight', url: `${FLUENT_CDN}/Flashlight/3D/flashlight_3d.png` },
    { name: 'Television', url: `${FLUENT_CDN}/Television/3D/television_3d.png` },
    { name: 'Camera', url: `${FLUENT_CDN}/Camera/3D/camera_3d.png` },
    { name: 'Video Camera', url: `${FLUENT_CDN}/Video%20camera/3D/video_camera_3d.png` },
    { name: 'Magnifying Glass', url: `${FLUENT_CDN}/Magnifying%20glass%20tilted%20left/3D/magnifying_glass_tilted_left_3d.png` },
    { name: 'Microphone', url: `${FLUENT_CDN}/Microphone/3D/microphone_3d.png` },
    { name: 'Headphone', url: `${FLUENT_CDN}/Headphone/3D/headphone_3d.png` },
    { name: 'Satellite', url: `${FLUENT_CDN}/Satellite%20antenna/3D/satellite_antenna_3d.png` },
    { name: 'Robot', url: `${FLUENT_CDN}/Robot/3D/robot_3d.png` },
    { name: 'Gear', url: `${FLUENT_CDN}/Gear/3D/gear_3d.png` },
  ],
  'People & Teams': [
    { name: 'Busts In Silhouette', url: `${FLUENT_CDN}/Busts%20in%20silhouette/3D/busts_in_silhouette_3d.png` },
    { name: 'Bust In Silhouette', url: `${FLUENT_CDN}/Bust%20in%20silhouette/3D/bust_in_silhouette_3d.png` },
    { name: 'People Holding Hands', url: `${FLUENT_CDN}/People%20holding%20hands/3D/people_holding_hands_3d.png` },
    { name: 'Handshake', url: `${FLUENT_CDN}/Handshake/3D/handshake_3d.png` },
    { name: 'Waving Hand', url: `${FLUENT_CDN}/Waving%20hand/3D/waving_hand_3d.png` },
    { name: 'Raised Hand', url: `${FLUENT_CDN}/Raised%20hand/3D/raised_hand_3d.png` },
    { name: 'Thumbs Up', url: `${FLUENT_CDN}/Thumbs%20up/3D/thumbs_up_3d.png` },
    { name: 'Thumbs Down', url: `${FLUENT_CDN}/Thumbs%20down/3D/thumbs_down_3d.png` },
    { name: 'Clapping Hands', url: `${FLUENT_CDN}/Clapping%20hands/3D/clapping_hands_3d.png` },
    { name: 'Folded Hands', url: `${FLUENT_CDN}/Folded%20hands/3D/folded_hands_3d.png` },
    { name: 'Flexed Biceps', url: `${FLUENT_CDN}/Flexed%20biceps/3D/flexed_biceps_3d.png` },
    { name: 'Brain', url: `${FLUENT_CDN}/Brain/3D/brain_3d.png` },
    { name: 'Eyes', url: `${FLUENT_CDN}/Eyes/3D/eyes_3d.png` },
    { name: 'Ear', url: `${FLUENT_CDN}/Ear/3D/ear_3d.png` },
    { name: 'Speaking Head', url: `${FLUENT_CDN}/Speaking%20head/3D/speaking_head_3d.png` },
    { name: 'Person Raising Hand', url: `${FLUENT_CDN}/Person%20raising%20hand/3D/person_raising_hand_3d.png` },
    { name: 'Man Office Worker', url: `${FLUENT_CDN}/Man%20office%20worker/3D/man_office_worker_3d.png` },
    { name: 'Woman Office Worker', url: `${FLUENT_CDN}/Woman%20office%20worker/3D/woman_office_worker_3d.png` },
    { name: 'Person Technologist', url: `${FLUENT_CDN}/Technologist/3D/technologist_3d.png` },
    { name: 'Student', url: `${FLUENT_CDN}/Student/3D/student_3d.png` },
  ],
  'Communication': [
    { name: 'Speech Balloon', url: `${FLUENT_CDN}/Speech%20balloon/3D/speech_balloon_3d.png` },
    { name: 'Thought Balloon', url: `${FLUENT_CDN}/Thought%20balloon/3D/thought_balloon_3d.png` },
    { name: 'Loudspeaker', url: `${FLUENT_CDN}/Loudspeaker/3D/loudspeaker_3d.png` },
    { name: 'Megaphone', url: `${FLUENT_CDN}/Megaphone/3D/megaphone_3d.png` },
    { name: 'Bell', url: `${FLUENT_CDN}/Bell/3D/bell_3d.png` },
    { name: 'Telephone', url: `${FLUENT_CDN}/Telephone/3D/telephone_3d.png` },
    { name: 'E-Mail', url: `${FLUENT_CDN}/E-mail/3D/e-mail_3d.png` },
    { name: 'Envelope', url: `${FLUENT_CDN}/Envelope/3D/envelope_3d.png` },
    { name: 'Incoming Envelope', url: `${FLUENT_CDN}/Incoming%20envelope/3D/incoming_envelope_3d.png` },
    { name: 'Outbox Tray', url: `${FLUENT_CDN}/Outbox%20tray/3D/outbox_tray_3d.png` },
    { name: 'Inbox Tray', url: `${FLUENT_CDN}/Inbox%20tray/3D/inbox_tray_3d.png` },
    { name: 'Pencil', url: `${FLUENT_CDN}/Pencil/3D/pencil_3d.png` },
    { name: 'Pen', url: `${FLUENT_CDN}/Pen/3D/pen_3d.png` },
    { name: 'Notebook', url: `${FLUENT_CDN}/Notebook/3D/notebook_3d.png` },
    { name: 'Books', url: `${FLUENT_CDN}/Books/3D/books_3d.png` },
    { name: 'Link', url: `${FLUENT_CDN}/Link/3D/link_3d.png` },
    { name: 'Paperclip', url: `${FLUENT_CDN}/Paperclip/3D/paperclip_3d.png` },
    { name: 'Pushpin', url: `${FLUENT_CDN}/Pushpin/3D/pushpin_3d.png` },
    { name: 'Bookmark', url: `${FLUENT_CDN}/Bookmark/3D/bookmark_3d.png` },
    { name: 'Newspaper', url: `${FLUENT_CDN}/Newspaper/3D/newspaper_3d.png` },
  ],
  'Goals & Success': [
    { name: 'Direct Hit', url: `${FLUENT_CDN}/Direct%20hit/3D/direct_hit_3d.png` },
    { name: 'Trophy', url: `${FLUENT_CDN}/Trophy/3D/trophy_3d.png` },
    { name: 'Gold Medal', url: `${FLUENT_CDN}/1st%20place%20medal/3D/1st_place_medal_3d.png` },
    { name: 'Silver Medal', url: `${FLUENT_CDN}/2nd%20place%20medal/3D/2nd_place_medal_3d.png` },
    { name: 'Bronze Medal', url: `${FLUENT_CDN}/3rd%20place%20medal/3D/3rd_place_medal_3d.png` },
    { name: 'Sports Medal', url: `${FLUENT_CDN}/Sports%20medal/3D/sports_medal_3d.png` },
    { name: 'Military Medal', url: `${FLUENT_CDN}/Military%20medal/3D/military_medal_3d.png` },
    { name: 'Star', url: `${FLUENT_CDN}/Star/3D/star_3d.png` },
    { name: 'Glowing Star', url: `${FLUENT_CDN}/Glowing%20star/3D/glowing_star_3d.png` },
    { name: 'Sparkles', url: `${FLUENT_CDN}/Sparkles/3D/sparkles_3d.png` },
    { name: 'Fire', url: `${FLUENT_CDN}/Fire/3D/fire_3d.png` },
    { name: 'Collision', url: `${FLUENT_CDN}/Collision/3D/collision_3d.png` },
    { name: 'High Voltage', url: `${FLUENT_CDN}/High%20voltage/3D/high_voltage_3d.png` },
    { name: 'Rocket', url: `${FLUENT_CDN}/Rocket/3D/rocket_3d.png` },
    { name: 'Check Mark', url: `${FLUENT_CDN}/Check%20mark%20button/3D/check_mark_button_3d.png` },
    { name: 'Hundred Points', url: `${FLUENT_CDN}/Hundred%20points/3D/hundred_points_3d.png` },
    { name: 'Party Popper', url: `${FLUENT_CDN}/Party%20popper/3D/party_popper_3d.png` },
    { name: 'Confetti Ball', url: `${FLUENT_CDN}/Confetti%20ball/3D/confetti_ball_3d.png` },
    { name: 'Crown', url: `${FLUENT_CDN}/Crown/3D/crown_3d.png` },
    { name: 'Rainbow', url: `${FLUENT_CDN}/Rainbow/3D/rainbow_3d.png` },
  ],
  'Process & Tools': [
    { name: 'Gear', url: `${FLUENT_CDN}/Gear/3D/gear_3d.png` },
    { name: 'Wrench', url: `${FLUENT_CDN}/Wrench/3D/wrench_3d.png` },
    { name: 'Hammer', url: `${FLUENT_CDN}/Hammer/3D/hammer_3d.png` },
    { name: 'Hammer and Wrench', url: `${FLUENT_CDN}/Hammer%20and%20wrench/3D/hammer_and_wrench_3d.png` },
    { name: 'Nut and Bolt', url: `${FLUENT_CDN}/Nut%20and%20bolt/3D/nut_and_bolt_3d.png` },
    { name: 'Toolbox', url: `${FLUENT_CDN}/Toolbox/3D/toolbox_3d.png` },
    { name: 'Magnet', url: `${FLUENT_CDN}/Magnet/3D/magnet_3d.png` },
    { name: 'Scissors', url: `${FLUENT_CDN}/Scissors/3D/scissors_3d.png` },
    { name: 'Key', url: `${FLUENT_CDN}/Key/3D/key_3d.png` },
    { name: 'Old Key', url: `${FLUENT_CDN}/Old%20key/3D/old_key_3d.png` },
    { name: 'Locked', url: `${FLUENT_CDN}/Locked/3D/locked_3d.png` },
    { name: 'Unlocked', url: `${FLUENT_CDN}/Unlocked/3D/unlocked_3d.png` },
    { name: 'Shield', url: `${FLUENT_CDN}/Shield/3D/shield_3d.png` },
    { name: 'Test Tube', url: `${FLUENT_CDN}/Test%20tube/3D/test_tube_3d.png` },
    { name: 'Microscope', url: `${FLUENT_CDN}/Microscope/3D/microscope_3d.png` },
    { name: 'Telescope', url: `${FLUENT_CDN}/Telescope/3D/telescope_3d.png` },
    { name: 'Hourglass', url: `${FLUENT_CDN}/Hourglass%20done/3D/hourglass_done_3d.png` },
    { name: 'Counterclockwise', url: `${FLUENT_CDN}/Counterclockwise%20arrows%20button/3D/counterclockwise_arrows_button_3d.png` },
    { name: 'Recycling', url: `${FLUENT_CDN}/Recycling%20symbol/3D/recycling_symbol_3d.png` },
    { name: 'Compass', url: `${FLUENT_CDN}/Compass/3D/compass_3d.png` },
  ],
  'Hearts & Emotions': [
    { name: 'Red Heart', url: `${FLUENT_CDN}/Red%20heart/3D/red_heart_3d.png` },
    { name: 'Orange Heart', url: `${FLUENT_CDN}/Orange%20heart/3D/orange_heart_3d.png` },
    { name: 'Yellow Heart', url: `${FLUENT_CDN}/Yellow%20heart/3D/yellow_heart_3d.png` },
    { name: 'Green Heart', url: `${FLUENT_CDN}/Green%20heart/3D/green_heart_3d.png` },
    { name: 'Blue Heart', url: `${FLUENT_CDN}/Blue%20heart/3D/blue_heart_3d.png` },
    { name: 'Purple Heart', url: `${FLUENT_CDN}/Purple%20heart/3D/purple_heart_3d.png` },
    { name: 'Sparkling Heart', url: `${FLUENT_CDN}/Sparkling%20heart/3D/sparkling_heart_3d.png` },
    { name: 'Growing Heart', url: `${FLUENT_CDN}/Growing%20heart/3D/growing_heart_3d.png` },
    { name: 'Smiling Face', url: `${FLUENT_CDN}/Smiling%20face%20with%20smiling%20eyes/3D/smiling_face_with_smiling_eyes_3d.png` },
    { name: 'Grinning Face', url: `${FLUENT_CDN}/Grinning%20face/3D/grinning_face_3d.png` },
    { name: 'Star Struck', url: `${FLUENT_CDN}/Star-struck/3D/star-struck_3d.png` },
    { name: 'Thinking Face', url: `${FLUENT_CDN}/Thinking%20face/3D/thinking_face_3d.png` },
    { name: 'Smiling Eyes', url: `${FLUENT_CDN}/Smiling%20face%20with%20heart-eyes/3D/smiling_face_with_heart-eyes_3d.png` },
    { name: 'Sunglasses', url: `${FLUENT_CDN}/Smiling%20face%20with%20sunglasses/3D/smiling_face_with_sunglasses_3d.png` },
    { name: 'Partying Face', url: `${FLUENT_CDN}/Partying%20face/3D/partying_face_3d.png` },
    { name: 'Hugging Face', url: `${FLUENT_CDN}/Smiling%20face%20with%20open%20hands/3D/smiling_face_with_open_hands_3d.png` },
    { name: 'Money Face', url: `${FLUENT_CDN}/Money-mouth%20face/3D/money-mouth_face_3d.png` },
    { name: 'Nerd Face', url: `${FLUENT_CDN}/Nerd%20face/3D/nerd_face_3d.png` },
    { name: 'Zany Face', url: `${FLUENT_CDN}/Zany%20face/3D/zany_face_3d.png` },
    { name: 'Exploding Head', url: `${FLUENT_CDN}/Exploding%20head/3D/exploding_head_3d.png` },
  ],
  'Nature': [
    { name: 'Sun', url: `${FLUENT_CDN}/Sun/3D/sun_3d.png` },
    { name: 'Full Moon', url: `${FLUENT_CDN}/Full%20moon/3D/full_moon_3d.png` },
    { name: 'Cloud', url: `${FLUENT_CDN}/Cloud/3D/cloud_3d.png` },
    { name: 'Snowflake', url: `${FLUENT_CDN}/Snowflake/3D/snowflake_3d.png` },
    { name: 'Droplet', url: `${FLUENT_CDN}/Droplet/3D/droplet_3d.png` },
    { name: 'Leaf', url: `${FLUENT_CDN}/Leaf%20fluttering%20in%20wind/3D/leaf_fluttering_in_wind_3d.png` },
    { name: 'Four Leaf Clover', url: `${FLUENT_CDN}/Four%20leaf%20clover/3D/four_leaf_clover_3d.png` },
    { name: 'Seedling', url: `${FLUENT_CDN}/Seedling/3D/seedling_3d.png` },
    { name: 'Evergreen Tree', url: `${FLUENT_CDN}/Evergreen%20tree/3D/evergreen_tree_3d.png` },
    { name: 'Palm Tree', url: `${FLUENT_CDN}/Palm%20tree/3D/palm_tree_3d.png` },
    { name: 'Rose', url: `${FLUENT_CDN}/Rose/3D/rose_3d.png` },
    { name: 'Sunflower', url: `${FLUENT_CDN}/Sunflower/3D/sunflower_3d.png` },
    { name: 'Blossom', url: `${FLUENT_CDN}/Blossom/3D/blossom_3d.png` },
    { name: 'Cherry Blossom', url: `${FLUENT_CDN}/Cherry%20blossom/3D/cherry_blossom_3d.png` },
    { name: 'Tulip', url: `${FLUENT_CDN}/Tulip/3D/tulip_3d.png` },
    { name: 'Cactus', url: `${FLUENT_CDN}/Cactus/3D/cactus_3d.png` },
    { name: 'Globe', url: `${FLUENT_CDN}/Globe%20showing%20Americas/3D/globe_showing_americas_3d.png` },
    { name: 'World Map', url: `${FLUENT_CDN}/World%20map/3D/world_map_3d.png` },
    { name: 'Mountain', url: `${FLUENT_CDN}/Mountain/3D/mountain_3d.png` },
    { name: 'Beach', url: `${FLUENT_CDN}/Beach%20with%20umbrella/3D/beach_with_umbrella_3d.png` },
  ],
  'Food & Drink': [
    { name: 'Coffee', url: `${FLUENT_CDN}/Hot%20beverage/3D/hot_beverage_3d.png` },
    { name: 'Tea', url: `${FLUENT_CDN}/Teacup%20without%20handle/3D/teacup_without_handle_3d.png` },
    { name: 'Tropical Drink', url: `${FLUENT_CDN}/Tropical%20drink/3D/tropical_drink_3d.png` },
    { name: 'Wine Glass', url: `${FLUENT_CDN}/Wine%20glass/3D/wine_glass_3d.png` },
    { name: 'Clinking Glasses', url: `${FLUENT_CDN}/Clinking%20glasses/3D/clinking_glasses_3d.png` },
    { name: 'Pizza', url: `${FLUENT_CDN}/Pizza/3D/pizza_3d.png` },
    { name: 'Hamburger', url: `${FLUENT_CDN}/Hamburger/3D/hamburger_3d.png` },
    { name: 'French Fries', url: `${FLUENT_CDN}/French%20fries/3D/french_fries_3d.png` },
    { name: 'Popcorn', url: `${FLUENT_CDN}/Popcorn/3D/popcorn_3d.png` },
    { name: 'Birthday Cake', url: `${FLUENT_CDN}/Birthday%20cake/3D/birthday_cake_3d.png` },
    { name: 'Cupcake', url: `${FLUENT_CDN}/Cupcake/3D/cupcake_3d.png` },
    { name: 'Cookie', url: `${FLUENT_CDN}/Cookie/3D/cookie_3d.png` },
    { name: 'Doughnut', url: `${FLUENT_CDN}/Doughnut/3D/doughnut_3d.png` },
    { name: 'Ice Cream', url: `${FLUENT_CDN}/Ice%20cream/3D/ice_cream_3d.png` },
    { name: 'Apple', url: `${FLUENT_CDN}/Red%20apple/3D/red_apple_3d.png` },
    { name: 'Avocado', url: `${FLUENT_CDN}/Avocado/3D/avocado_3d.png` },
    { name: 'Grapes', url: `${FLUENT_CDN}/Grapes/3D/grapes_3d.png` },
    { name: 'Watermelon', url: `${FLUENT_CDN}/Watermelon/3D/watermelon_3d.png` },
    { name: 'Strawberry', url: `${FLUENT_CDN}/Strawberry/3D/strawberry_3d.png` },
    { name: 'Banana', url: `${FLUENT_CDN}/Banana/3D/banana_3d.png` },
  ],
  'Travel & Transport': [
    { name: 'Car', url: `${FLUENT_CDN}/Automobile/3D/automobile_3d.png` },
    { name: 'Taxi', url: `${FLUENT_CDN}/Taxi/3D/taxi_3d.png` },
    { name: 'Bus', url: `${FLUENT_CDN}/Bus/3D/bus_3d.png` },
    { name: 'Racing Car', url: `${FLUENT_CDN}/Racing%20car/3D/racing_car_3d.png` },
    { name: 'Airplane', url: `${FLUENT_CDN}/Airplane/3D/airplane_3d.png` },
    { name: 'Rocket', url: `${FLUENT_CDN}/Rocket/3D/rocket_3d.png` },
    { name: 'Flying Saucer', url: `${FLUENT_CDN}/Flying%20saucer/3D/flying_saucer_3d.png` },
    { name: 'Helicopter', url: `${FLUENT_CDN}/Helicopter/3D/helicopter_3d.png` },
    { name: 'Sailboat', url: `${FLUENT_CDN}/Sailboat/3D/sailboat_3d.png` },
    { name: 'Ship', url: `${FLUENT_CDN}/Ship/3D/ship_3d.png` },
    { name: 'Train', url: `${FLUENT_CDN}/Train/3D/train_3d.png` },
    { name: 'High Speed Train', url: `${FLUENT_CDN}/High-speed%20train/3D/high-speed_train_3d.png` },
    { name: 'House', url: `${FLUENT_CDN}/House/3D/house_3d.png` },
    { name: 'House with Garden', url: `${FLUENT_CDN}/House%20with%20garden/3D/house_with_garden_3d.png` },
    { name: 'Office Building', url: `${FLUENT_CDN}/Office%20building/3D/office_building_3d.png` },
    { name: 'Hospital', url: `${FLUENT_CDN}/Hospital/3D/hospital_3d.png` },
    { name: 'School', url: `${FLUENT_CDN}/School/3D/school_3d.png` },
    { name: 'Castle', url: `${FLUENT_CDN}/Castle/3D/castle_3d.png` },
    { name: 'Church', url: `${FLUENT_CDN}/Church/3D/church_3d.png` },
    { name: 'Stadium', url: `${FLUENT_CDN}/Stadium/3D/stadium_3d.png` },
  ],
};

const IconPicker = ({ onSelect, selectedIcon, selectedType = 'image' }) => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('icons');
  const [iconCategory, setIconCategory] = useState('Business');
  const [imageUrl, setImageUrl] = useState('');

  // Filter icons based on search
  const filteredIcons = useMemo(() => {
    const categoryIcons = fluent3DIcons[iconCategory] || [];
    if (!search) return categoryIcons;
    return categoryIcons.filter(icon => 
      icon.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, iconCategory]);

  // Search across all categories
  const allFilteredIcons = useMemo(() => {
    if (!search) return null;
    const allIcons = Object.values(fluent3DIcons).flat();
    return allIcons.filter(icon => 
      icon.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  const handleIconSelect = (icon) => {
    onSelect({ type: 'image', value: icon.url, name: icon.name });
  };

  const handleImageUrlSubmit = () => {
    if (imageUrl.trim()) {
      onSelect({ type: 'image', value: imageUrl.trim(), name: 'Custom' });
      setImageUrl('');
    }
  };

  const iconsToDisplay = search ? allFilteredIcons : filteredIcons;

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-4 h-11">
          <TabsTrigger value="icons" className="text-sm">
            🎨 3D Icons
          </TabsTrigger>
          <TabsTrigger value="custom" className="text-sm">
            <Link2 className="h-4 w-4 mr-2" />
            Custom URL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="icons" className="mt-0 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10"
            />
          </div>

          {/* Category selector - dropdown style for cleaner look */}
          {!search && (
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(fluent3DIcons).map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setIconCategory(category)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                    iconCategory === category 
                      ? 'bg-indigo-600 text-white font-medium' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Icons Grid */}
          <ScrollArea className="h-[320px] rounded-lg border bg-white">
            <div className="p-3">
              <div className="grid grid-cols-6 gap-2">
                {iconsToDisplay?.map((icon, idx) => (
                  <button
                    key={`${icon.name}-${idx}`}
                    type="button"
                    onClick={() => handleIconSelect(icon)}
                    className={`aspect-square p-2 rounded-lg flex items-center justify-center transition-all ${
                      selectedType === 'image' && selectedIcon === icon.url 
                        ? 'bg-indigo-50 ring-2 ring-indigo-500' 
                        : 'hover:bg-slate-50'
                    }`}
                    title={icon.name}
                  >
                    <img 
                      src={icon.url} 
                      alt={icon.name}
                      className="w-10 h-10 object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.opacity = '0.3'; }}
                    />
                  </button>
                ))}
              </div>
              {iconsToDisplay?.length === 0 && (
                <p className="text-center text-slate-400 py-8 text-sm">No icons found</p>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="custom" className="mt-0">
          <div className="space-y-4 p-6 border rounded-lg bg-slate-50">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-lg bg-white border flex items-center justify-center">
                {selectedType === 'image' && selectedIcon ? (
                  <img src={selectedIcon} alt="Custom" className="w-10 h-10 object-contain" />
                ) : (
                  <Upload className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <p className="text-sm text-slate-500">Paste an image URL</p>
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="https://example.com/icon.png"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="flex-1 h-10"
              />
              <Button onClick={handleImageUrlSubmit} disabled={!imageUrl.trim()} size="sm" className="h-10 px-4">
                Add
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Display a selected icon (for preview)
export const IconDisplay = ({ icon, className = "h-10 w-10" }) => {
  if (!icon) return null;
  
  if (icon.type === 'image' || icon.url) {
    const url = icon.value || icon.url;
    return (
      <img 
        src={url} 
        alt={icon.name || 'Icon'} 
        className={`${className} object-contain`}
        onError={(e) => { e.target.style.opacity = '0.3'; }}
      />
    );
  }
  
  // Fallback for emoji type
  if (icon.type === 'emoji') {
    return <span className="text-4xl leading-none">{icon.value}</span>;
  }
  
  return null;
};

export default IconPicker;
